import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/utils';
import { AlertTriangle, Mail, MessageCircle, Search } from 'lucide-react';
import api from '@/lib/api';

export default function TopDefaulters() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(30);

  useEffect(() => {
    loadDefaulters();
  }, [limit]);

  const loadDefaulters = async () => {
    try {
      const response = await api.get(`/defaulters/top?limit=${limit}`);
      setData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar inadimplentes');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-6">Carregando...</div>;
  }

  const filteredDefaulters = data?.defaulters.filter(d =>
    d.name.toLowerCase().includes(search.toLowerCase()) ||
    d.email.toLowerCase().includes(search.toLowerCase())
  ) || [];

  return (
    <div className="p-6 space-y-6" data-testid="defaulters-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Inadimplentes</h1>
        <p className="text-muted-foreground mt-1">Gestão de cobranças e inadimplência</p>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-destructive/30 bg-destructive/5">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Inadimplentes</CardTitle>
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-destructive">{data?.total_count || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">Responsáveis com débitos</p>
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Valor Total</CardTitle>
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-destructive">{formatCurrency(data?.total_outstanding || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Em débito atualizado</p>
          </CardContent>
        </Card>

        <Card className="border-border/60">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Valor Médio</CardTitle>
            <AlertTriangle className="h-5 w-5 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-600">
              {formatCurrency((data?.total_outstanding || 0) / (data?.total_count || 1))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Por inadimplente</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-10"
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="search-defaulters"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={limit === 10 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLimit(10)}
          >
            Top 10
          </Button>
          <Button
            variant={limit === 30 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLimit(30)}
          >
            Top 30
          </Button>
          <Button
            variant={limit === 100 ? 'default' : 'outline'}
            size="sm"
            onClick={() => setLimit(100)}
          >
            Top 100
          </Button>
        </div>
      </div>

      {/* Tabela */}
      <Card className="border-border/60">
        <CardHeader>
          <CardTitle>Lista de Inadimplentes</CardTitle>
          <CardDescription>Mostrando {filteredDefaulters.length} de {data?.total_count || 0} registros</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Posição</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead>Débito</TableHead>
                <TableHead>Mês</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDefaulters.map((defaulter, idx) => (
                <TableRow key={defaulter.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        idx < 3 ? 'bg-destructive text-white' :
                        idx < 10 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {idx + 1}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{defaulter.name}</p>
                      <p className="text-xs text-muted-foreground">{defaulter.type}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {defaulter.whatsapp && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <MessageCircle className="w-3 h-3" />
                          {defaulter.whatsapp}
                        </div>
                      )}
                      {defaulter.email && (
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Mail className="w-3 h-3" />
                          {defaulter.email}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-bold text-destructive text-lg">
                      {formatCurrency(defaulter.outstanding_balance)}
                    </span>
                  </TableCell>
                  <TableCell>{defaulter.month}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {defaulter.whatsapp_link && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(defaulter.whatsapp_link, '_blank')}
                          data-testid="whatsapp-button"
                        >
                          <MessageCircle className="w-3 h-3 mr-1" />
                          WhatsApp
                        </Button>
                      )}
                      {defaulter.email && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.location.href = `mailto:${defaulter.email}`}
                        >
                          <Mail className="w-3 h-3 mr-1" />
                          Email
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
